"use client";

import React from "react";
import { Megaphone, Map, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const services = [{
  id: 1,
  title: "Salesforce Partner Ecosystem",
  description: "Strategic SI, ISV, and OEM implementation services designed to architect complex, high-performance CRM environments.",
  iconName: "Megaphone"
}, {
  id: 2,
  title: "Apple Business Innovation",
  description: "Sophisticated device management and ecosystem integration that leverages Apple's security and performance for enterprise.",
  iconName: "Map"
}, {
  id: 3,
  title: "Next-Gen AI Infrastructure",
  description: "Engineering scalable compute gateways and crypto-integrated platforms like OceanClaw and OceanWallet for sovereign intelligence.",
  iconName: "TrendingUp"
}];
const serviceSteps = [{
  serviceId: 1,
  stepId: "s1-1",
  title: "Solution Architecture",
  content: "Designing robust API architectures and custom Salesforce data models that serve as the backbone of your digital operations."
}, {
  serviceId: 1,
  stepId: "s1-2",
  title: "ISV & OEM Development",
  content: "Building proprietary products on the Salesforce platform, from initial concept to AppExchange listing and commercial scaling."
}, {
  serviceId: 1,
  stepId: "s1-3",
  title: "Enterprise Systems Integration",
  content: "Seamlessly connecting Salesforce with your broader tech stack to ensure data fluidity and real-time business intelligence."
}, {
  serviceId: 2,
  stepId: "s2-1",
  title: "Managed Deployment",
  content: "Expert-led Apple Business Manager setup and zero-touch deployment strategies for global, distributed workforces."
}, {
  serviceId: 2,
  stepId: "s2-2",
  title: "Sovereign Security",
  content: "Implementing advanced MDM policies and hardware-level encryption to protect sensitive corporate data across all Apple endpoints."
}, {
  serviceId: 2,
  stepId: "s2-3",
  title: "Ecosystem Optimization",
  content: "Maximizing the power of the Apple ecosystem through custom application development and hardware-software synergy."
}, {
  serviceId: 3,
  stepId: "s3-1",
  title: "OceanClaw Compute",
  content: "Unlocking infinitely scalable AI compute through our proprietary gateway, designed for high-intensity processing tasks."
}, {
  serviceId: 3,
  stepId: "s3-2",
  title: "OceanWallet Integration",
  content: "Enabling secure network processing rewards with stablecoin earnings and immediate liquidity via USDC swaps."
}, {
  serviceId: 3,
  stepId: "s3-3",
  title: "Applied AI Infrastructure",
  content: "Building the physical and digital foundations for personal agent work and decentralized intelligence networks."
}];
const iconMap: Record<string, React.ElementType> = {
  Megaphone,
  Map,
  TrendingUp
};
export default function ComprehensiveManagement() {
  return <section data-section-id="132456" id="enterprise-integration" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-20">
        <div className="bg-primary/30 h-[500px] w-[500px] rounded-full blur-[120px]" />
      </div>

      <div className="container relative mx-auto px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <Sparkles className="text-primary size-5" />
            <span className="text-secondary text-sm font-default font-bold tracking-wider uppercase">
              Sovereign Intelligence
            </span>
          </div>
          <h2 className="font-default text-4xl font-bold tracking-tight sm:text-5xl text-foreground">
            Enterprise Architecture
          </h2>
          <p className="text-muted-foreground mt-6 text-lg leading-8 font-default">
            Sophisticated engineering across Salesforce, Apple Business, and decentralized AI compute infrastructure for modern sovereign enterprises.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 lg:grid-cols-3">
          {services.map((service, index) => {
          const Icon = iconMap[service.iconName];
          const steps = serviceSteps.filter(step => step.serviceId === service.id);
          return <div data-index={index} key={service.id} className="group relative h-full">
                {}
                <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-0 blur-sm transition-opacity duration-500 group-hover:opacity-100" />
                
                <Card className="bg-card/80 border-border/50 relative flex h-full flex-col overflow-hidden backdrop-blur-xl transition-colors duration-500 hover:bg-card">
                  <CardHeader className="pb-4">
                    <div className="mb-6 flex items-center justify-between">
                      <div className="relative">
                        <div className="bg-primary/20 absolute inset-0 rounded-full blur-md transition-all duration-500 group-hover:bg-primary/40 group-hover:blur-xl" />
                        <div className="bg-background/50 border-border/50 relative flex size-14 items-center justify-center rounded-xl border backdrop-blur-sm">
                          {Icon && <Icon className="text-primary size-7" />}
                        </div>
                      </div>
                      <span className="text-muted-foreground/30 font-mono text-4xl font-bold">
                        0{index + 1}
                      </span>
                    </div>
                    <CardTitle className="text-2xl font-default text-foreground">{service.title}</CardTitle>
                    <CardDescription className="text-muted-foreground mt-2 text-base font-default">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto pt-6">
                    <div className="bg-background/50 rounded-lg p-1 backdrop-blur-sm">
                      <Accordion type="single" collapsible className="w-full">
                        {steps.map((step, stepIndex) => <AccordionItem data-index={stepIndex} key={step.stepId} value={step.stepId} className="border-border/50 px-4 last:border-0">
                            <AccordionTrigger className="hover:text-primary text-sm font-semibold hover:no-underline font-default text-foreground">
                              {step.title}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground text-sm leading-relaxed font-default">
                              {step.content}
                            </AccordionContent>
                          </AccordionItem>)}
                      </Accordion>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-20 flex justify-center">
          <Button asChild size="lg" className="group rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#">
              Explore Our Systems
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}